import React, { useState, useEffect } from "react"
import { IoIosCloseCircleOutline } from "react-icons/io"
import { FiSearch } from "react-icons/fi"
import axios from "axios"
import Spinner from "../Spinner"
import api, { BASE_URL } from "../../utils/api"
import { FaRegTrashAlt } from "react-icons/fa"
import Pagination from "../Pagination"
import useDeBounce from "../../utils/useDeBounce"
import {
  PAGE_SIZE,
  mapPotentialMember,
  getMemberTypeForApi,
  getExistingMemberIds,
  handleBulkAddMembersResponse,
} from "../../utils/groupMemberUtils"
import { ErrorNotification } from "../../utils"

const GROUP_TYPE_LABELS = {
  farmers: "Farmers",
  processors: "Processors",
  service_providers: "Service Providers",
}

export default function AddGroupMembersModal({
  groupId,
  groupType,
  existingMembers = [],
  onClose,
  onSuccess,
}) {
  const [searchText, setSearchText] = useState("")
  const debouncedSearch = useDeBounce(searchText, 400)
  const [availableMembers, setAvailableMembers] = useState([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedMembers, setSelectedMembers] = useState([])
  const [existingMemberIds, setExistingMemberIds] = useState(() =>
    getExistingMemberIds(existingMembers)
  )
  const [adding, setAdding] = useState(false)

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const selectedIds = new Set(selectedMembers.map((m) => m.value))

  useEffect(() => {
    setExistingMemberIds(getExistingMemberIds(existingMembers))
  }, [existingMembers])

  useEffect(() => {
    if (!groupId) return

    const fetchAllExistingIds = async () => {
      try {
        const response = await api.get(`groups/${groupId}/members`, {
          params: { page: 1, page_size: 1000 },
        })
        const list = response.data.results || response.data.members || []
        setExistingMemberIds(getExistingMemberIds(list))
      } catch {
        setExistingMemberIds(getExistingMemberIds(existingMembers))
      }
    }

    fetchAllExistingIds()
  }, [groupId])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, groupType])

  useEffect(() => {
    if (!groupType) {
      setAvailableMembers([])
      setTotalCount(0)
      return
    }

    const fetchMembers = async () => {
      setMembersLoading(true)
      const token = localStorage.getItem("token")

      try {
        if (groupType === "farmers") {
          const resp = await api.get("farmers/", {
            params: {
              page: currentPage,
              page_size: PAGE_SIZE,
              search: debouncedSearch || undefined,
            },
          })
          const results = (resp.data.results || [])
            .map((f) =>
              mapPotentialMember({
                id: f.id,
                firstname: f.firstname,
                lastname: f.lastname,
                member_type: "farmer",
              })
            )
            .filter((m) => !existingMemberIds.has(m.value))

          setAvailableMembers(results)
          setTotalCount(resp.data.count || 0)
        } else {
          let url = `${BASE_URL}groups/potential_members/?group_type=${groupType}`
          if (debouncedSearch) {
            url += `&search=${encodeURIComponent(debouncedSearch)}`
          }
          const resp = await axios.get(url, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          })
          const allMembers = (resp.data.members || [])
            .map(mapPotentialMember)
            .filter((m) => !existingMemberIds.has(m.value))

          const start = (currentPage - 1) * PAGE_SIZE
          setAvailableMembers(allMembers.slice(start, start + PAGE_SIZE))
          setTotalCount(allMembers.length)
        }
      } catch {
        setAvailableMembers([])
        setTotalCount(0)
      } finally {
        setMembersLoading(false)
      }
    }

    fetchMembers()
  }, [groupType, debouncedSearch, currentPage, existingMemberIds])

  const toggleMember = (member) => {
    if (selectedIds.has(member.value)) {
      setSelectedMembers(selectedMembers.filter((m) => m.value !== member.value))
    } else {
      setSelectedMembers([...selectedMembers, member])
    }
  }

  const removeMember = (value) => {
    setSelectedMembers(selectedMembers.filter((m) => m.value !== value))
  }

  const clearAll = () => setSelectedMembers([])

  const addNewMembers = async () => {
    if (selectedMembers.length === 0) {
      ErrorNotification("Please select at least one member to add")
      return
    }

    setAdding(true)
    const token = localStorage.getItem("token")
    const membersPayload = selectedMembers.map((m) => ({
      member_type: getMemberTypeForApi(groupType),
      member_id: m.member_id,
    }))

    try {
      const response = await fetch(`${BASE_URL}groups/${groupId}/bulk_add_members/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ members: membersPayload }),
      })
      const data = await response.json()

      const { shouldClose } = handleBulkAddMembersResponse(data, {
        onSuccess: () => {
          onSuccess?.()
          setSelectedMembers([])
        },
        onClose,
      })

      if (shouldClose) {
        onClose?.()
      }
    } catch {
      ErrorNotification("An error occurred while adding new members")
    } finally {
      setAdding(false)
    }
  }

  const listTitle =
    groupType === "processors"
      ? "Available Processors"
      : groupType === "service_providers"
        ? "Available Service Providers"
        : "Available Farmers"

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md p-4">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] relative flex flex-col overflow-hidden">
        <div className="flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark">
          <div>
            <h2 className="text-xl font-bold dark:text-white">Add New Members</h2>
            {groupType && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Group type: {GROUP_TYPE_LABELS[groupType] || groupType}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-6 w-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x dark:divide-border-dark flex-1 min-h-0 overflow-hidden">
          {/* Left: selected members */}
          <div className="flex flex-col px-4 py-5 md:px-6 overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-yellowcolor text-lg font-semibold">
                Selected Members
                {selectedMembers.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({selectedMembers.length})
                  </span>
                )}
              </h3>
              {selectedMembers.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="flex items-center text-red-500 text-sm hover:underline"
                >
                  Clear all <FaRegTrashAlt className="ml-1" />
                </button>
              )}
            </div>

            {selectedMembers.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center border border-dashed dark:border-border-dark rounded-lg flex-1 flex items-center justify-center">
                Select members from the list on the right
              </p>
            ) : (
              <ul className="overflow-y-auto space-y-2 border dark:border-border-dark rounded-lg p-2 flex-1">
                {selectedMembers.map((member) => (
                  <li
                    key={member.value}
                    className="flex items-center justify-between bg-gray-50 dark:bg-secondaryDark rounded-md px-3 py-2 text-sm dark:text-white"
                  >
                    <span>{member.label}</span>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-500 ml-2 shrink-0"
                      onClick={() => removeMember(member.value)}
                      aria-label={`Remove ${member.label}`}
                    >
                      &times;
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Right: searchable paginated list */}
          <div className="flex flex-col min-h-[300px] lg:min-h-0 overflow-hidden">
            <div className="px-4 py-4 md:px-6 border-b dark:border-border-dark flex-shrink-0">
              <h3 className="text-yellowcolor text-lg font-semibold mb-3">{listTitle}</h3>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full pl-10 bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                  placeholder="Search by name"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 md:px-6">
              {membersLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Spinner />
                </div>
              ) : availableMembers.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-500 text-sm text-center">
                  No available members found
                </div>
              ) : (
                <ul className="divide-y dark:divide-border-dark">
                  {availableMembers.map((member) => {
                    const isSelected = selectedIds.has(member.value)
                    return (
                      <li key={member.value}>
                        <label className="flex items-center gap-3 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-secondaryDark rounded-md px-2 -mx-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleMember(member)}
                            className="h-4 w-4 rounded border-gray-300 text-yellowcolor focus:ring-yellowcolor"
                          />
                          <span className="text-sm dark:text-white">{member.label}</span>
                        </label>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {groupType && totalCount > PAGE_SIZE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </div>

        <div className="border-t p-4 border-gray-200 flex flex-row items-center justify-end gap-4 bg-white dark:bg-dark flex-shrink-0 dark:border-border-dark">
          <button
            type="button"
            onClick={onClose}
            className="px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg"
          >
            Close
          </button>
          <button
            type="button"
            onClick={addNewMembers}
            className="px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg font-semibold hover:bg-yellow-400 disabled:opacity-50"
            disabled={adding || selectedMembers.length === 0}
          >
            {adding ? "Adding..." : "Add Members"}
          </button>
        </div>
      </div>
    </div>
  )
}
